/**
 * useShellRollActions - sheet rolls from both panels.
 *
 * Two `useRollActions` instances: the ATTACKER's, whose right-click opposes
 * the current defender, and the DEFENDER's, which rolls off the current
 * tab's character (or its derived target fields), attributed to the
 * target's name, and whose right-click opposes the acting character.
 *
 * Each side keeps its own transient xN badge overrides for the Overview
 * sheets - sheet rolls only, never the stored character, never attacks or
 * reactions. Extracted from DeathWatchSimulator.
 */
import { effectiveSheet } from '../engine/roster/stancedSheet';
import { useState, useCallback } from 'react';
import useRollActions from './useRollActions';

/** A badge override map with one characteristic set, or cleared with null. */
export function withOverride(prev, charId, multiplier) {
  const next = { ...prev };
  if (multiplier == null) delete next[charId];
  else next[charId] = multiplier;
  return next;
}

/** One side's override map and its stable handler. */
function useUnnaturalOverrides() {
  const [overrides, setOverrides] = useState({});
  const onOverride = useCallback(
    (charId, multiplier) => setOverrides((prev) => withOverride(prev, charId, multiplier)),
    [],
  );
  return [overrides, onOverride];
}

/** Who the attacker's right-click opposes: the current defender. */
function defenderAsOpponent(currentTab) {
  if (!currentTab) return null;
  return {
    name: currentTab.target?.name || currentTab.label || 'Target',
    character: currentTab.sourceCharacter || null,
    target: currentTab.target || null,
  };
}

/**
 * @param {object} args - actor, currentTab, unnaturalDosMode, addRollNarrative.
 * @returns the two roll-action records and each side's overrides and handler.
 */
export default function useShellRollActions({ actor, currentTab, unnaturalDosMode, addRollNarrative }) {
  const [attackerUnnaturalOverrides, onAttackerUnnaturalOverride] = useUnnaturalOverrides();
  const [defenderUnnaturalOverrides, onDefenderUnnaturalOverride] = useUnnaturalOverrides();
  const rollActions = useRollActions({
    // The effective sheet: a stance's bonus to a skill counts in the test.
    character: actor ? effectiveSheet(actor) : null,
    unnaturalDosMode,
    unnaturalOverrides: attackerUnnaturalOverrides,
    addRollNarrative,
    opponent: defenderAsOpponent(currentTab),
  });
  const defenderRollActions = useRollActions({
    character: currentTab?.sourceCharacter || null,
    characterName: currentTab?.target?.name || currentTab?.label || null,
    target: currentTab?.target || null,
    fallbackName: 'Defender',
    unnaturalDosMode,
    unnaturalOverrides: defenderUnnaturalOverrides,
    addRollNarrative,
    opponent: actor?.sheet ? { name: actor.name || 'Attacker', character: effectiveSheet(actor), target: null } : null,
  });
  return {
    rollActions,
    defenderRollActions,
    attackerUnnaturalOverrides,
    onAttackerUnnaturalOverride,
    defenderUnnaturalOverrides,
    onDefenderUnnaturalOverride,
  };
}
